import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { DEVTOOLS_OPTIONS } from '@/modules/debug/constants/zustand';
import { UnifiedVehicle, UnifiedVehicleDetails } from '@/modules/vehicles/types';

type State = {
  allVehiclesState: UnifiedVehicle[]; // get from pimCore
  vehicleDetailsState: UnifiedVehicleDetails[]; // get from pimCore
};

const INITIAL_STATE: State = {
  allVehiclesState: [],
  vehicleDetailsState: [],
};

type Action = {
  allVehicles: {
    set(vehicles: UnifiedVehicle[]): void;
    getAll(): UnifiedVehicle[];
    getByIds(ids: string[]): UnifiedVehicle[];
    clear(): void;
  };
  vehicleDetails: {
    set(vehicles: UnifiedVehicleDetails[]): void;
    getAll(): UnifiedVehicleDetails[];
    getByIds(ids: string[]): UnifiedVehicleDetails[];
    clear(): void;
  };
  hasOmniVehicles(): boolean;
  reset(): void;
};

export const useVehicleStore = create<State & Action>()(
  devtools(
    (set, get) => ({
      ...INITIAL_STATE,

      allVehicles: {
        set(vehicles) {
          set(
            {
              allVehiclesState: vehicles,
            },
            undefined,
            {
              type: 'allVehicles/set',
            },
          );
        },

        getAll() {
          return get().allVehiclesState;
        },

        getByIds(ids) {
          return get().allVehiclesState.filter((v) => ids.includes(v.id));
        },

        clear() {
          set(
            {
              allVehiclesState: [],
            },
            undefined,
            {
              type: 'allVehicles/clear',
            },
          );
        },
      },

      vehicleDetails: {
        set(vehicles) {
          set(
            {
              vehicleDetailsState: vehicles,
            },
            undefined,
            {
              type: 'vehicleDetails/set',
            },
          );
        },

        getAll() {
          return get().vehicleDetailsState;
        },

        getByIds(ids) {
          return get().vehicleDetailsState.filter((v) => ids.includes(v.id));
        },

        clear() {
          set(
            {
              vehicleDetailsState: [],
            },
            undefined,
            {
              type: 'vehicleDetails/clear',
            },
          );
        },
      },

      hasOmniVehicles() {
        return get().vehicleDetailsState.some((v) => v.omni);
      },

      reset() {
        set(INITIAL_STATE, undefined, {
          type: 'reset',
        });
      },
    }),
    { store: 'vehicleStore', ...DEVTOOLS_OPTIONS },
  ),
);
